// Ошибки API в формате бэкенда: {"errors":[{"message","code","field"?}]}
import { FIELD_MESSAGES } from './fieldMessages';

// Все коды бэкенда — копия epl_backend internal/errcode/errcode.go, в том же порядке
export const API_ERROR_CODES = [
  // 422 по полям
  'required',
  'invalid',
  'invalid_date',
  'underage',
  'invalid_snils',
  'invalid_license_ser',
  'invalid_license_number',
  'invalid_license_issue_date',
  'invalid_license_expire_date',
  'invalid_email',
  'invalid_sex',
  // Регистрация водителя и приветственный экран
  'app_not_launched',
  'registration_required',
  'account_inactive',
  'already_registered',
  'email_in_use',
  'login_in_use',
  'phone_in_use',
  'not_a_driver',
  'registration_incomplete',
  'driver_not_registered',
  'forbidden_user',
  'admin_only',
  // Запросы, которые сервер не смог прочитать
  'invalid_body',
  'invalid_request',
  'invalid_query',
  'repeated_query_parameter',
  'invalid_pagination',
  'invalid_status',
  'invalid_amount',
  'invalid_autopark_id',
  'invalid_payout_uuid',
  // Автомобили, осмотры, путевые листы
  'car_forbidden',
  'car_field_admin_only',
  'driver_not_in_park',
  'check_status_final',
  'roadlist_status_final',
  'status_not_settable',
  // Платежи, тарифы, финансы автопарков
  'payment_rejected',
  'payment_method_not_found',
  'receipt_error',
  'tariff_not_found',
  'price_locked',
  'autopark_required',
  'autopark_not_found',
  'autopark_not_settable',
  'autopark_has_finance_records',
  'autopark_has_payouts',
  'balance_not_settable',
  'paid_not_settable',
  'record_not_found',
  'record_has_payouts',
  'record_without_autopark',
  'payout_exceeds_remaining',
  'payout_exceeds_park_balance',
  'payout_uuid_conflict',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

// Незнакомый код не сохраняем: сравнивать его не с чем
function toApiErrorCode(value: unknown): ApiErrorCode | undefined {
  return API_ERROR_CODES.find((code) => code === value);
}

// Ошибка поля (422, а также 409 login_in_use / phone_in_use), field — имя поля в теле запроса
export interface IFieldError {
  field: string;
  code?: ApiErrorCode;
  message: string;
}

// Ошибка API с HTTP-статусом и кодом из errors[0].code
export class ApiError extends Error {
  status?: number;
  code?: ApiErrorCode;
  fieldErrors: IFieldError[];

  constructor(message: string, status?: number, code?: ApiErrorCode, fieldErrors: IFieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

// Запись из errors[]: до проверки её поля могут быть чем угодно
interface IApiErrorEntry {
  message?: unknown;
  code?: unknown;
  field?: unknown;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Непустая строка, иначе undefined
function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function errorEntries(data: unknown): IApiErrorEntry[] {
  if (!isObject(data) || !Array.isArray(data.errors)) return [];
  return data.errors.filter(isObject);
}

function toFieldErrors(entries: IApiErrorEntry[]): IFieldError[] {
  return entries.flatMap(({ field, code, message }) => {
    const name = nonEmptyString(field);
    if (!name) return [];
    return [{ field: name, code: toApiErrorCode(code), message: nonEmptyString(message) ?? '' }];
  });
}

// error — то, что бросает api.ts ({ response: { status, data } }), или любая другая ошибка
export function toApiError(error: unknown, fallback: string): ApiError {
  const response = isObject(error) && isObject(error.response) ? error.response : {};
  const entries = errorEntries(response.data);
  const first = entries[0];
  // Старый формат ответа: { message }
  const legacyMessage = isObject(response.data) ? nonEmptyString(response.data.message) : undefined;
  return new ApiError(
    nonEmptyString(first?.message) ?? legacyMessage ?? fallback,
    typeof response.status === 'number' ? response.status : undefined,
    toApiErrorCode(first?.code),
    toFieldErrors(entries),
  );
}

// Принимает ApiError или payload из rejectWithValue ({ status, code })
export function hasApiErrorCode(error: unknown, status: number, ...codes: ApiErrorCode[]): boolean {
  const { status: actual, code } = (error ?? {}) as { status?: number; code?: string };
  return actual === status && codes.some((expected) => expected === code);
}

// Ошибки по полям, которые форма показывает, — текстом сервера (первая запись на поле).
// Поле без своего input пропускаем: иначе общий текст ошибки сменился бы меткой, которую никто не увидит.
export function pickFieldErrors<F extends string>(error: unknown, fields: readonly F[]): Partial<Record<F, string>> {
  if (!(error instanceof ApiError)) return {};
  const picked: Partial<Record<F, string>> = {};
  error.fieldErrors.forEach(({ field, message }) => {
    const name = fields.find((known) => known === field);
    if (name && !picked[name]) picked[name] = message || FIELD_MESSAGES.invalid;
  });
  return picked;
}
